import { useEffect, useState } from "react";

import { AppNavigation, AppStepper, TierStepper } from "@zenith/hooks";
import Clock from "@zenith/assets/icons/Clock";
import {
  DropZoneComponent,
  ModalComponent,
  SelectComponent,
  SpinnerComponent,
} from "@zenith/components";
import { GreenTick } from "@zenith/assets/icons/components";
import { UPGRADE_ACCOUNT } from "@zenith/routes/paths";
import {
  FILE_INVALID_TYPE_BACK_OFFICE,
  MIN_SIZE_BACK_OFFICE,
} from "@zenith/data/constants";
import {
  ApiResponseDTOBusinessAddressDTO,
  ApiResponseDTODiscardUpgradeResponseDTO,
  BusinessAddressDTO,
  BusinessApiService,
  DocumentApiService,
  DocumentDTO,
  TierApiService,
  TierStepsResponseDTO,
} from "@zenith/services/user";
import CloseCircle from "@zenith/assets/icons/CloseCircle";
import { SelectItemsType } from "@zenith/components/ui/SelectComponent/select-component/interfaces";
import { useUpgradeContext } from "@zenith/context";
import StepStatusComponent from "@zenith/features/Tiers/components/StepStatusComponent/step-status-component/step-status-component";
import { getFileExtension, updateTierStepStatus } from "@zenith/utils/Helpers";
import { removeUnderscores } from "@zenith/utils/Formaters/removeUnderscore";
import { FormErrorsType, ProofOfAddressPageProps } from "./interfaces";

type ProofOfAddressFormData = {
  country: string;
  documentType: BusinessAddressDTO.documentType | "";
  documentContent: string;
  mimeExt: string;
};

function ProofOfAddressPage(props: ProofOfAddressPageProps) {
  const {
    step: { stepStatus },
  } = props;

  const { setActiveStep } = AppStepper();
  const { goToNextOpenStep } = TierStepper();
  const { navigateTo } = AppNavigation();
  const { upgradeContext, setUpgradeContext, clearUpgradeContext } =
    useUpgradeContext();
  const { isRecoveryFlow } = upgradeContext;

  const isAlreadyApproved =
    stepStatus === TierStepsResponseDTO.stepStatus.APPROVED;
  const proofOfAddressInitialStatus =
    isRecoveryFlow || isAlreadyApproved ? stepStatus : undefined;
  const [backModal, setBackModal] = useState<boolean>(false);
  const [selectedDocument, setSelectedDocument] = useState<DocumentDTO>();
  const [selectItems, setSelectItems] = useState<SelectItemsType[]>([]);
  const [documents, setDocuments] = useState<DocumentDTO[]>([]);

  const [proofOfAddressStartPage, setProofOfAddressStartPage] =
    useState<boolean>(!isRecoveryFlow && !isAlreadyApproved);

  const initialFormData: ProofOfAddressFormData = {
    country: "Nigeria",
    documentType: "" as ProofOfAddressFormData["documentType"],
    documentContent: "",
    mimeExt: "",
  };
  const [formData, setFormData] =
    useState<ProofOfAddressFormData>(initialFormData);

  const [formErrors, setFormErrors] = useState<FormErrorsType>({
    documentContent: { message: "", hasError: false },
  });

  const [proofOfAddressStatus, setProofOfAddressStatus] = useState<
    string | undefined
  >(proofOfAddressInitialStatus);
  const [proofOfAddressError, setProofOfAddressError] = useState<string>("");
  const [isContinueDisabled, setIsContinueDisabled] = useState<boolean>(false);

  const [selectedDocumentError, setSelectedDocumentError] = useState<{
    hasError: boolean;
    message: string;
  }>({ hasError: false, message: "" });

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const res = await DocumentApiService.getDocumentSelect(
          "NIGERIA_PROOF_ADDRESS"
        );
        if (res.data && res?.data?.documents) {
          const newSelectItemsArray: SelectItemsType[] = [];
          const docs = res.data.documents;
          res.data.documents.forEach((e) => {
            newSelectItemsArray.push({
              label: e.text || "",
              value: e.code || "",
            });
          });
          setSelectItems(newSelectItemsArray);
          setDocuments(docs);
        }
      } catch (error) {
        console.error(error);
      }
    };
    fetchDocuments();
  }, []);

  const isFormValid = (): boolean => {
    return (
      formData.documentType !== "" &&
      formData.documentContent !== "" &&
      formData.mimeExt !== "" &&
      !formErrors.documentContent.hasError
    );
  };

  const getImage = (image: File | null) => {
    if (!image) return;
    const reader = new FileReader();
    reader.readAsDataURL(image);
    reader.onload = function () {
      const result = reader.result as string;
      const rawBase64 = result.split(",")[1];
      setFormData((prev) => ({
        ...prev,
        documentContent: rawBase64,
        mimeExt: getFileExtension(image),
      }));
      setFormErrors((prev) => ({
        ...prev,
        documentContent: { message: "", hasError: false },
      }));
    };
  };

  const deleteImage = () => {
    setFormData((prev) => ({
      ...prev,
      documentContent: "",
      mimeExt: "",
    }));
  };

  const handleModalState = () => {
    setBackModal(!backModal);
  };

  const handleContinueButton = () => {
    goToNextOpenStep();
  };

  const handleTryAgain = () => {
    setFormData(initialFormData);
    setSelectedDocument(undefined);
    setProofOfAddressStatus(undefined);
    setProofOfAddressError("");
    setProofOfAddressStartPage(true);
  };

  const handleDiscardUpgrade = () => {
    TierApiService.discardUpgradeTier()
      .then((response: ApiResponseDTODiscardUpgradeResponseDTO) => {
        if ("data" in response && response.data) {
          setActiveStep(0);
          clearUpgradeContext();
          navigateTo(UPGRADE_ACCOUNT);
        }
      })
      .catch((error) => {
        return error;
      });
  };

  const onSelectChange = (
    event: React.SyntheticEvent<Element, Event> | null,
    newValue: string | null
  ) => {
    const newSelectedDocument = documents?.find(
      (item) => item.code === newValue
    );

    setFormData((prev) => ({
      ...prev,
      documentType: (newSelectedDocument?.code ??
        "") as ProofOfAddressFormData["documentType"],
      documentContent: "",
      mimeExt: "",
    }));

    setFormErrors((prev) => ({
      ...prev,
      documentContent: { message: "", hasError: false },
    }));

    setSelectedDocument(newSelectedDocument);
  };

  const onSelectBlur = (newValue: string) => {
    if (!newValue) {
      setSelectedDocumentError({
        hasError: true,
        message: "Proof of Address Document is required",
      });
      return;
    }
    setSelectedDocumentError({ hasError: false, message: "" });
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    // Stay on the form while the upload runs - the spinner is on Continue -
    // so the page doesn't swap to a narrower loading screen and back.
    setIsContinueDisabled(true);
    setProofOfAddressError("");

    BusinessApiService.postBusinessAddress(
      formData as unknown as BusinessAddressDTO
    )
      .then((response: ApiResponseDTOBusinessAddressDTO) => {
        setIsContinueDisabled(false);
        setProofOfAddressStartPage(false);

        if (response?.success === false) {
          setProofOfAddressStatus(BusinessAddressDTO.verificationStatus.FAILED);
          setProofOfAddressError(
            response.message ||
              "Information verification failed. Please try again."
          );
          setSelectedDocument(undefined);
          return;
        }

        // Any accepted upload waits for back-office review, whatever
        // verificationStatus the response carries (or none), so there is
        // nothing to poll for - the step stays pending until it is reviewed.
        setProofOfAddressStatus(BusinessAddressDTO.verificationStatus.PENDING);
        setUpgradeContext({
          ...upgradeContext,
          tierSteps: updateTierStepStatus(
            upgradeContext,
            TierStepsResponseDTO.stepName.PROOF_OF_ADDRESS_STEP,
            TierStepsResponseDTO.stepStatus.PENDING
          ),
        });
      })
      .catch((error) => {
        setIsContinueDisabled(false);
        setProofOfAddressStartPage(false);

        const errorMessage =
          error?.body?.message ||
          "Information verification failed. Please try again.";

        setProofOfAddressStatus(BusinessAddressDTO.verificationStatus.FAILED);
        setProofOfAddressError(errorMessage);
        setFormData(initialFormData);
        setSelectedDocument(undefined);
      })
      .finally(() => setFormData(initialFormData));
  };

  return (
    <>
      <ModalComponent
        body="Are you sure you want to leave?"
        secondLine="All information provided will be lost."
        title="Discard Upgrade"
        showModal={backModal}
        onClickClose={handleModalState}
        doubleOptions={true}
        onLeftButtonClick={handleDiscardUpgrade}
        onRightButtonClick={handleModalState}
        leftButtonText="Discard Upgrade"
        rightButtonText="Return to Upgrade"
      />

      <div className="mb-5 overflow-hidden">
        {proofOfAddressStartPage ? (
          <form onSubmit={handleSubmit}>
            <div className="mb-5 overflow-hidden">
              <h1 className="text-neutral-850 mb-4 text-4.5xl font-medium">
                Proof of Address
              </h1>
              <div className="mb-6 min-w-0 text-base text-neutral-600">
                Kindly upload a valid proof of address document
              </div>
            </div>

            <div>
              <div className="my-5">
                <p className="my-3 text-sm font-light text-neutral-200">
                  Please ensure that the document provided clearly displays your
                  address and is dated within the last 3 months.
                </p>
                <p className="my-3 text-sm font-light text-neutral-200">
                  Max file size is 10MB. Supported file types are jpeg, png and
                  pdf.
                </p>
              </div>
              <div className="mb-4">
                <SelectComponent
                  items={selectItems}
                  className="w-full rounded-xl"
                  listboxZIndex={1300}
                  label="Document Type"
                  id="documentType"
                  labelId="documentTypeLabel"
                  placeholder="Select document type"
                  defaultValue=""
                  hasError={selectedDocumentError.hasError}
                  errorMessage={selectedDocumentError.message}
                  onChange={onSelectChange}
                  onClose={onSelectBlur}
                  editable={true}
                />
              </div>
              {selectedDocument && (
                <div className="mb-6 cursor-pointer">
                  <DropZoneComponent
                    fieldName={removeUnderscores(formData.documentType)}
                    getImage={getImage}
                    deleteImage={deleteImage}
                    parentFileChangeListener={selectedDocument}
                    documentType={formData.documentType}
                    acceptedFileTypes={["jpeg", "png", "pdf"]}
                    acceptedMinSize={MIN_SIZE_BACK_OFFICE}
                    fileInvalidTypeErrorMessage={FILE_INVALID_TYPE_BACK_OFFICE}
                  />
                </div>
              )}
            </div>

            <div className="stepper-buttons-container">
              <button
                id="discard-button"
                className="secondary-button mt-12"
                onClick={handleModalState}
                type="button"
              >
                Discard
              </button>
              <button
                id="continue-button"
                className="primary-button mt-12"
                type="submit"
                disabled={!isFormValid() || isContinueDisabled}
              >
                {isContinueDisabled ? (
                  <SpinnerComponent size={20} type="secondary" />
                ) : (
                  "Continue"
                )}
              </button>
            </div>
          </form>
        ) : (
          <div className="mt-6 flex flex-col">
            <h1 className="text-neutral-850 mb-16 text-4.5xl font-medium">
              Proof of Address
            </h1>

            {(proofOfAddressStatus ===
              BusinessAddressDTO.verificationStatus.SUCCESS ||
              proofOfAddressStatus ===
                TierStepsResponseDTO.stepStatus.APPROVED) && (
              <StepStatusComponent
                status={TierStepsResponseDTO.stepStatus.APPROVED}
                title={"Information Successfully Verified"}
                subTitle={'Press "Continue" to proceed.'}
                icon={<GreenTick transform="scale(1.70)" />}
                handleDiscardButton={handleModalState}
                handleContinueButton={handleContinueButton}
              />
            )}

            {(proofOfAddressStatus ===
              BusinessAddressDTO.verificationStatus.PENDING ||
              proofOfAddressStatus ===
                TierStepsResponseDTO.stepStatus.PENDING) && (
              <StepStatusComponent
                status={TierStepsResponseDTO.stepStatus.PENDING}
                title={"Waiting For Approval"}
                subTitle={'Press "Continue" to proceed.'}
                icon={<Clock transform="scale(1.70)" />}
                handleDiscardButton={handleModalState}
                handleContinueButton={handleContinueButton}
              />
            )}

            {(proofOfAddressStatus ===
              BusinessAddressDTO.verificationStatus.FAILED ||
              proofOfAddressStatus ===
                TierStepsResponseDTO.stepStatus.REJECTED) && (
              <StepStatusComponent
                status={BusinessAddressDTO.verificationStatus.FAILED}
                title={"Verification Failed"}
                subTitle={proofOfAddressError || "Please try again."}
                icon={<CloseCircle transform="scale(1.70)" />}
                handleDiscardButton={handleModalState}
                handleTryAgainButton={handleTryAgain}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default ProofOfAddressPage;
